import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FaHeart, FaArrowLeft } from "react-icons/fa";
import { AuthContext } from "../../routes/AuthContext";
import { toast } from "react-toastify";
import { addToWishlist, removeFromWishlist } from "../wishlist/Wishlist";
import { addToCart } from "../cart/Cart";
import "../product/ProductCard.css";
import "./ViewallProducts.css";

const ViewAllProducts = () => { 
  const { categoryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const { getCustomerData, isAuthenticated, setCartCount } = useContext(AuthContext);
  const customer = getCustomerData();
  const customer_id = customer?.customer_id;

  const [categories, setCategories]   = useState([]);
  const [products, setProducts]       = useState([]);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [addingId, setAddingId]       = useState(null);
  const [cartQtyMap, setCartQtyMap]   = useState({});

  const [selectedCategoryId, setSelectedCategoryId] = useState(
    categoryId ? String(categoryId) : null
  );
  const [selectedCategoryName, setSelectedCategoryName] = useState(
    location.state?.categoryName || "All Products"
  );

  /* ── Fetch Categories & Products ── */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          axios.post(`${BASE_URL}product_categories`),
          axios.post(`${BASE_URL}products`),
        ]);
        if (catRes.data.api_success)  setCategories(catRes.data.data);
        if (prodRes.data.api_success) setProducts(prodRes.data.products);
      } catch (e) {
        console.error("Fetch error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [BASE_URL]);

  /* ── Fetch Wishlist ── */
  useEffect(() => {
    if (!customer_id || !isAuthenticated) return;
    const fetchWishlist = async () => {
      try {
        const fd = new FormData();
        fd.append("customer_id", customer_id);
        const res = await axios.post(`${BASE_URL}customer_wish_list_details`, fd);
        if (res.data.api_success) {
          setWishlistIds(res.data.wishlist_items.map((i) => i.product_id));
        }
      } catch (e) {
        console.warn("Wishlist fetch skipped:", e.message);
      }
    };
    fetchWishlist();
  }, [customer_id, isAuthenticated, BASE_URL]);

  /* ── Derived: filtered products ── */
  const filteredProducts = selectedCategoryId
    ? products.filter(
        (p) => String(p.product_category_id) === String(selectedCategoryId)
      )
    : products;

  /* ── Category click — scroll sidebar item into view ── */
  const handleCategoryClick = (cat) => {
    setSelectedCategoryId(String(cat.product_category_id));
    setSelectedCategoryName(cat.product_category_name);
    const el = document.getElementById(`cat-${cat.product_category_id}`);
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    // also scroll product area back to top
    const main = document.querySelector(".vap-main");
    if (main) main.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ── Wishlist toggle ── */
  const toggleWishlist = async (e, product_id) => {
    e.stopPropagation();
    if (!isAuthenticated || !customer_id) {
      toast.warning("Please login to use wishlist");
      return;
    }
    try {
      const isWishlisted = wishlistIds.includes(product_id);
      if (isWishlisted) {
        await removeFromWishlist(customer_id, product_id);
        setWishlistIds((prev) => prev.filter((id) => id !== product_id));
        toast.info("Removed from wishlist");
      } else {
        await addToWishlist(customer_id, product_id);
        setWishlistIds((prev) => [...prev, product_id]);
        toast.success("Added to wishlist");
      }
    } catch (e) {
      console.warn("Wishlist toggle error:", e.message);
    }
  };

  /* ── Add to cart ── */
  const handleAddToCart = async (e, product_id) => {
    e.stopPropagation();
    if (!isAuthenticated || !customer_id) {
      toast.warning("Please login to add items to cart");
      return;
    }
    if (addingId === product_id) return;
    try {
      setAddingId(product_id);
      const currentQty = cartQtyMap[product_id] || 0;
      const newQty = currentQty + 1;
      const res = await addToCart(customer_id, product_id, newQty, setCartCount, 1);
      if (res.api_success) {
        setCartQtyMap((prev) => ({ ...prev, [product_id]: newQty }));
        toast.success(currentQty === 0 ? "Added to cart" : "Quantity increased");
      } else {
        toast.error(res.message || "Failed to add to cart");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setTimeout(() => setAddingId(null), 500);
    }
  };

  if (loading) {
    return <p style={{ textAlign: "center", marginTop: 40 }}>Loading...</p>;
  }

  return (
    <div className="vap-page">
      <div className="vap-page-inner">

        {/* ═══════════ SIDEBAR — always visible ═══════════ */}
        <aside className="vap-sidebar">
          {categories.map((cat) => {
            const isActive =
              String(cat.product_category_id) === String(selectedCategoryId);
            return (
              <div
                id={`cat-${cat.product_category_id}`}
                key={cat.product_category_id}
                className={`vap-sidebar-item ${isActive ? "active" : ""}`}
                onClick={() => handleCategoryClick(cat)}
              >
                <div className="vap-sidebar-img-wrap">
                  <img src={cat.category_logo} alt={cat.product_category_name} />
                </div>
                <p className="vap-sidebar-label">{cat.product_category_name}</p>
              </div>
            );
          })}
        </aside>

        {/* ═══════════ MAIN ═══════════ */}
        <main className="vap-main">

          {/* Header */}
          <div className="vap-header">
            <button className="vap-back-btn" onClick={() => navigate(-1)}>
              <FaArrowLeft />
            </button>
            <h2 className="category-heading" style={{ margin: 0 }}>
              {selectedCategoryName}
            </h2>
            <span className="vap-count">{filteredProducts.length} items</span>
          </div>

          {/* Products */}
          {filteredProducts.length === 0 ? (
            <p style={{ textAlign: "center", color: "#888", marginTop: 40 }}>
              No products found in this category.
            </p>
          ) : (
            <div className="vap-product-grid">
              {filteredProducts.map((item) => {
                const isWishlisted = wishlistIds.includes(item.id);
                return (
                  <div
                    className="product-card"
                    key={item.id}
                    onClick={() => navigate(`/product-details/${item.id}`)}
                  >
                    <div className="product-image-wrapper">
                      <button
                        className={`favorite-btn ${isWishlisted ? "active" : ""}`}
                        onClick={(e) => toggleWishlist(e, item.id)}
                      >
                        <FaHeart />
                      </button>
                      <img
                        src={item.images?.[0] || "/placeholder.png"}
                        alt={item.product_name}
                        className="product-image"
                      />
                    </div>
                    <div className="product-info">
                      <h3 className="product-name">{item.product_name}</h3>
                      <div className="product-footer">
                        <div className="product-price-wrapper">
                          {item.cost_price && (
                            <span className="product-cost-price">
                              ₹{item.cost_price}
                            </span>
                          )}
                          {item.selling_price && (
                            <span className="product-selling-price">
                              ₹{item.selling_price}
                            </span>
                          )}
                        </div>
                        <button
                          className="add-btn"
                          onClick={(e) => handleAddToCart(e, item.id)}
                          disabled={addingId === item.id}
                          style={{
                            opacity: addingId === item.id ? 0.6 : 1,
                            cursor: addingId === item.id ? "not-allowed" : "pointer",
                          }}
                        >
                          {addingId === item.id ? "..." : "+"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>

      </div>
    </div>
  );
};

export default ViewAllProducts;